import { DisposableStack, disposeSymbol } from '@mui/x-internals/disposable';
import { getDateKey, TimeoutManager } from '@mui/x-scheduler-internals/internals';
const MAX_CONCURRENT_REQUESTS = 3;
const MAX_QUEUED_REQUESTS = 3;
export const DEBOUNCE_MS = 150;
export let RequestStatus = /*#__PURE__*/function (RequestStatus) {
  RequestStatus[RequestStatus["QUEUED"] = 0] = "QUEUED";
  RequestStatus[RequestStatus["PENDING"] = 1] = "PENDING";
  RequestStatus[RequestStatus["SETTLED"] = 2] = "SETTLED";
  RequestStatus[RequestStatus["UNKNOWN"] = 3] = "UNKNOWN";
  return RequestStatus;
}({});
/**
 * Generates a unique key for a date range using timestamps.
 * Format: "startTimestamp:endTimestamp"
 */
function getDateRangeKey(adapter, range) {
  const startTimestamp = getDateKey(range.start, adapter);
  const endTimestamp = getDateKey(range.end, adapter);
  return `${startTimestamp}:${endTimestamp}`;
}

/**
 * Fetches events from the data source with option to limit the number of concurrent requests.
 * Determines the status of a request based on the enum `RequestStatus`.
 * Uses date range keys to uniquely identify a request.
 *
 * Features:
 * - Debounces rapid successive requests
 * - Limits queued requests to prevent queue bloat
 * - Prioritizes the most recently queued request
 * - Skips already settled requests
 */
export class SchedulerDataManager {
  pendingRequests = new Map();
  queuedRequests = new Map();
  settledRequests = new Set();
  disposables = new DisposableStack();

  // Registered first so `clearAll` runs last (LIFO) — the constructor's defer
  // calls `cancelQueuedRequests`, which itself touches `timeoutManager`.
  timeoutManager = this.disposables.use(new TimeoutManager());

  // Requests waiting for the debounce timer to finish (rapid navigation buffer)
  stagedRanges = null;
  pendingDebounceResolve = null;
  get disposed() {
    return this.disposables.disposed;
  }
  constructor(adapter, fetchFunction, options = {}) {
    this.adapter = adapter;
    this.fetchFunction = fetchFunction;
    this.maxConcurrentRequests = options.maxConcurrentRequests ?? MAX_CONCURRENT_REQUESTS;
    this.maxQueuedRequests = options.maxQueuedRequests ?? MAX_QUEUED_REQUESTS;
    this.debounceMs = options.debounceMs ?? DEBOUNCE_MS;
    this.disposables.defer(() => {
      this.cancelQueuedRequests();
      this.pendingRequests.clear();
      this.settledRequests.clear();
    });
  }

  /**
   * Helper to safely add ranges to the queue and enforce limits.
   */
  commitRangesToQueue = ranges => {
    ranges.forEach(range => {
      const key = getDateRangeKey(this.adapter, range);

      // Skip if already settled, currently fetching, or already in queue
      if (this.pendingRequests.has(key) || this.queuedRequests.has(key)) {
        return;
      }
      this.queuedRequests.set(key, range);
    });
    // Trim queue if it exceeds max size.
    while (this.queuedRequests.size > this.maxQueuedRequests) {
      const firstKey = this.queuedRequests.keys().next().value;
      if (firstKey !== undefined) {
        this.queuedRequests.delete(firstKey);
      }
    }
  };
  processQueue = async () => {
    if (this.queuedRequests.size === 0 || this.pendingRequests.size >= this.maxConcurrentRequests) {
      return;
    }
    const loopLength = Math.min(this.maxConcurrentRequests - this.pendingRequests.size, this.queuedRequests.size);
    if (loopLength === 0) {
      return;
    }
    const fetchQueue = Array.from(this.queuedRequests.entries());
    const fetchPromises = [];
    const startIndex = Math.max(0, fetchQueue.length - loopLength);
    const itemsToProcess = fetchQueue.slice(startIndex);
    for (const [rangeKey, range] of itemsToProcess) {
      this.queuedRequests.delete(rangeKey);
      this.pendingRequests.set(rangeKey, range);
      fetchPromises.push(this.fetchFunction(range, this.adapter));
    }
    await Promise.all(fetchPromises);
  };

  /**
   * The returned promise resolves when the debounce window flushes, NOT when this
   * specific call's data has been fetched. If a subsequent `queue()` arrives within
   * the debounce window, the previous promise resolves immediately and the new call
   * takes over — callers shouldn't treat the resolution as a fetch-completion signal.
   */
  queue = async ranges => {
    if (this.disposed) {
      return undefined;
    }
    if (this.pendingDebounceResolve) {
      this.pendingDebounceResolve();
      this.pendingDebounceResolve = null;
    }
    this.timeoutManager.clearTimeout('debounce');
    this.stagedRanges = [...ranges];
    return new Promise((resolve, reject) => {
      this.pendingDebounceResolve = resolve;
      this.timeoutManager.startTimeout('debounce', this.debounceMs, async () => {
        this.pendingDebounceResolve = null;
        // Move from Stage -> Actual Queue
        if (this.stagedRanges) {
          this.commitRangesToQueue(this.stagedRanges);
          this.stagedRanges = null;
        }
        try {
          await this.processQueue();
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    });
  };

  /**
   * Immediately processes the queue without debouncing.
   * Useful for initial load or forced refresh.
   */
  queueImmediate = async ranges => {
    if (this.disposed) {
      return;
    }
    // Clear any pending debounce
    this.timeoutManager.clearTimeout('debounce');
    if (this.pendingDebounceResolve) {
      this.pendingDebounceResolve();
      this.pendingDebounceResolve = null;
    }
    this.stagedRanges = null;
    this.commitRangesToQueue(ranges);
    await this.processQueue();
  };
  cancelQueuedRequests = () => {
    // Clear any pending debounce
    this.timeoutManager.clearTimeout('debounce');
    if (this.pendingDebounceResolve) {
      this.pendingDebounceResolve();
      this.pendingDebounceResolve = null;
    }
    this.stagedRanges = null;
    this.queuedRequests.clear();
  };
  setRequestSettled = async range => {
    if (this.disposed) {
      return;
    }
    const key = getDateRangeKey(this.adapter, range);
    this.pendingRequests.delete(key);
    this.settledRequests.add(key);
    await this.processQueue();
  };
  [disposeSymbol]() {
    this.disposables.dispose();
  }
  clearPendingRequest = async range => {
    const key = getDateRangeKey(this.adapter, range);
    this.pendingRequests.delete(key);
    await this.processQueue();
  };
  getRequestStatus = range => {
    const key = getDateRangeKey(this.adapter, range);
    if (this.pendingRequests.has(key)) {
      return RequestStatus.PENDING;
    }
    if (this.queuedRequests.has(key)) {
      return RequestStatus.QUEUED;
    }
    if (this.settledRequests.has(key)) {
      return RequestStatus.SETTLED;
    }
    return RequestStatus.UNKNOWN;
  };
  getActiveRequestsCount = () => this.pendingRequests.size + this.queuedRequests.size;
}